import { useMutation } from '@apollo/client';
import { gql } from 'graphql-tag';
import { useCallback, useState } from 'react';
import specificResourceFragment from '~/modules/resource-management/enhancers/specificResourceFragment';
import { updateUserSkillNudgesCache } from './updateUserSkillNudgesCache';

const APPLY_USER_DEFINED_SKILL_MODIFICATIONS_FOR_USER = gql`
  mutation ApplyUserDefinedSkillModificationsForUser(
    $input: ApplyUserDefinedSkillModificationsForUserInput!
  ) {
    applyUserDefinedSkillModificationsForUser(input: $input) {
      userId
      addedSkills {
        id
        uri
        displayText
        skillLevel {
          id
          displayText
          rating
        }
      }
      removedSkills
      updatedSkills {
        id
        uri
        displayText
        skillLevel {
          id
          displayText
          rating
        }
      }
    }
  }
`;

export const updateUserDefinedSkillsCache = ({
  cache,
  userId,
  addedSkills,
  updatedSkills,
  removedSkills
}) => {
  if (!userId) return;

  const cachedFragmentId = `ResourceDetails:${userId}`;
  let cachedResourceDetails = null;

  cachedResourceDetails = cache.readFragment({
    id: cachedFragmentId,
    variables: {
      isPsaPraaResourceMatchingEnabled: true
    },
    fragment: specificResourceFragment,
    fragmentName: 'SpecificResourceFragment'
  });

  if (!cachedResourceDetails) return;

  const entries = [];

  cachedResourceDetails.userDefinedSkills.forEach(r => {
    if (!removedSkills.includes(r.id)) {
      const updatedSkill = updatedSkills.find(u => u.id === r.id);

      if (updatedSkill) entries.push(updatedSkill);
      else entries.push(r);
    }
  });

  const skills = [...entries, ...addedSkills];

  cache.writeFragment({
    id: cachedFragmentId,
    variables: {
      isPsaPraaResourceMatchingEnabled: true
    },
    fragmentName: 'SpecificResourceFragment',
    fragment: specificResourceFragment,
    data: {
      ...cachedResourceDetails,
      userDefinedSkills: skills
    }
  });
};

export const useApplyUserDefinedSkillModificationsForUser = ({ userId }) => {
  const [
    applyUserDefinedSkillModificationsForUserError,
    setApplyUserDefinedSkillModificationsForUserError
  ] = useState(null);

  const [pendingNudgeIds, setPendingNudgeIds] = useState([]);

  const [applyUserDefinedSkillModificationsForUserMutation] = useMutation(
    APPLY_USER_DEFINED_SKILL_MODIFICATIONS_FOR_USER
  );

  const applyUserDefinedSkillModificationsForUser = useCallback(
    async ({ addSkills, removeSkills, updateSkills, nudgeIds = [] }) => {
      if (nudgeIds.length > 0) {
        setPendingNudgeIds(nudgeIds);
      }

      const { error } = await applyUserDefinedSkillModificationsForUserMutation(
        {
          variables: {
            input: {
              userId,
              addSkills,
              removeSkills,
              updateSkills
            }
          },
          update: (cache, mutationResponse) => {
            const result =
              mutationResponse.data
                ?.applyUserDefinedSkillModificationsForUser || {};

            updateUserDefinedSkillsCache({
              cache,
              userId,
              addedSkills: result.addedSkills || [],
              removedSkills: result.removedSkills || [],
              updatedSkills: result.updatedSkills || []
            });

            if (pendingNudgeIds.length > 0) {
              updateUserSkillNudgesCache({
                cache,
                userUri: userId,
                nudgeIds: pendingNudgeIds
              });
              setPendingNudgeIds([]);
            }
          }
        }
      );

      if (error) {
        setApplyUserDefinedSkillModificationsForUserError(error);
        setPendingNudgeIds([]);
      }

      return error;
    },
    [
      applyUserDefinedSkillModificationsForUserMutation,
      userId,
      pendingNudgeIds,
      setPendingNudgeIds
    ]
  );

  return {
    applyUserDefinedSkillModificationsForUser,
    applyUserDefinedSkillModificationsForUserError
  };
};

export default useApplyUserDefinedSkillModificationsForUser;
